import React from 'react'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ParticipantTile from './ParticipantTile'

export default function OverlayRail({
  participants = [],
  railStateId,
  videoBinder,
  onSelectParticipant,
  onExpandRail,
  compact = false,
  carousel = false,
  debugContext = null,
}) {
  const scrollContainerRef = React.useRef(null)
  const participantIdsKey = React.useMemo(
    () => participants.map((participant) => participant?.id || '').join('|'),
    [participants],
  )
  const [activeParticipantIndex, setActiveParticipantIndex] = React.useState(0)

  React.useEffect(() => {
    setActiveParticipantIndex((previousIndex) => {
      if (!participants.length) {
        return 0
      }

      return Math.min(previousIndex, participants.length - 1)
    })
  }, [participantIdsKey, participants.length])

  if (railStateId === 'collapsed') {
    return (
      <button
        type="button"
        className="shared-video-rail shared-video-rail--collapsed"
        onClick={onExpandRail}
        aria-label="Expand participant rail"
      >
        <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
        <span>Participants</span>
      </button>
    )
  }

  if (!participants.length) {
    return null
  }

  const isFilmstrip = railStateId === 'filmstrip'
  const showCarousel = carousel && !isFilmstrip
  const activeParticipant = showCarousel
    ? participants[activeParticipantIndex] || participants[0] || null
    : null
  const canRotateParticipants = participants.length > 1

  if (showCarousel && activeParticipant) {
    return (
      <div
        className={`shared-video-rail shared-video-rail--${railStateId}${
          compact ? ' shared-video-rail--compact' : ''
        } shared-video-rail--carousel`}
      >
        {canRotateParticipants ? (
          <button
            type="button"
            className="shared-video-rail__carousel-nav shared-video-rail__carousel-nav--previous"
            onClick={() => {
              setActiveParticipantIndex((previousIndex) =>
                previousIndex === 0 ? participants.length - 1 : previousIndex - 1,
              )
            }}
            aria-label="Show previous participant"
          >
            <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        ) : null}

        <div className="shared-video-rail__carousel-frame">
          <ParticipantTile
            key={activeParticipant.id}
            participant={activeParticipant}
            videoBinder={videoBinder}
            onClick={onSelectParticipant}
            debugContext={debugContext}
          />
        </div>

        {canRotateParticipants ? (
          <button
            type="button"
            className="shared-video-rail__carousel-nav shared-video-rail__carousel-nav--next"
            onClick={() => {
              setActiveParticipantIndex((previousIndex) =>
                previousIndex === participants.length - 1 ? 0 : previousIndex + 1,
              )
            }}
            aria-label="Show next participant"
          >
            <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
          </button>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={`shared-video-rail shared-video-rail--${railStateId}${
        compact ? ' shared-video-rail--compact' : ''
      }`}
    >
      {isFilmstrip ? (
        <button
          type="button"
          className="shared-video-rail__scroll"
          onClick={() => scrollContainerRef.current?.scrollBy({ left: -220, behavior: 'smooth' })}
          aria-label="Scroll rail left"
        >
          <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
        </button>
      ) : null}

      <div
        ref={scrollContainerRef}
        className={`shared-video-rail__items${
          isFilmstrip ? ' shared-video-rail__items--horizontal' : ''
        }`}
      >
        {participants.map((participant) => (
          <ParticipantTile
            key={participant.id}
            participant={participant}
            videoBinder={videoBinder}
            onClick={onSelectParticipant}
            debugContext={debugContext}
          />
        ))}
      </div>

      {isFilmstrip ? (
        <button
          type="button"
          className="shared-video-rail__scroll"
          onClick={() => scrollContainerRef.current?.scrollBy({ left: 220, behavior: 'smooth' })}
          aria-label="Scroll rail right"
        >
          <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
        </button>
      ) : null}
    </div>
  )
}

OverlayRail.displayName = 'OverlayRail'
